import React, { useEffect, useState } from "react";
import { ReportsDetailWrap } from "components/atoms/Reports/ReportDetail/ReportsDetals.styles";
import analyticsService from "services/analyticsService";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDateTime = dateStr => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
};

const STATUS_LABELS = {
    delivered: "Completed",
    cancelled: "Cancelled",
    out_for_delivery: "On Delivery",
    preparing: "Preparing",
    accepted: "Accepted",
    new_order: "New Order",
    ready_for_pickup: "Ready for Pickup",
};

const PAYMENT_LABELS = {
    credit_card: "Credit Card",
    cash_on_delivery: "Cash on Delivery",
};

// ─── Skeleton loader ──────────────────────────────────────────────────────────

const SkeletonLine = ({ width = "100%", height = "16px", mb = "8px" }) => (
    <div
        style={{
            width,
            height,
            borderRadius: 8,
            marginBottom: mb,
            background: "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.4s infinite",
        }}
    />
);

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * RepotsDetail (name kept for backward compat)
 *
 * Props:
 *   orderId — MongoDB ObjectId string of the order to load
 */
const RepotsDetail = ({ orderId }) => {
    const [loading, setLoading] = useState(false);
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!orderId) return;

        const fetchDetail = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await analyticsService.getOrderDetail(orderId);
                setOrder(res?.data || null);
            } catch (err) {
                setError(err.message || "Failed to load order details");
            } finally {
                setLoading(false);
            }
        };

        fetchDetail();
    }, [orderId]);

    // ─── Loading state ───────────────────────────────────────────────────────
    if (loading) {
        return (
            <ReportsDetailWrap>
                <div className="transaction-detail">
                    <SkeletonLine width="140px" height="20px" mb="20px" />
                    <div className="detail-wrap">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="detail-card">
                                <div className="card"><SkeletonLine /><SkeletonLine width="60%" /></div>
                                <div className="card"><SkeletonLine /><SkeletonLine width="60%" /></div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="purchase-items">
                    <SkeletonLine width="160px" height="20px" mb="20px" />
                    <div className="purchase-card">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="card">
                                <div className="image-wrap" style={{ width: 94, height: 80, background: "#f0f0f0", borderRadius: 10 }} />
                                <div className="text-wrap" style={{ flex: 1 }}>
                                    <SkeletonLine width="70%" height="14px" mb="8px" />
                                    <SkeletonLine width="50%" height="12px" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {/* shimmer keyframe */}
                <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
            </ReportsDetailWrap>
        );
    }

    // ─── Error state ─────────────────────────────────────────────────────────
    if (error) {
        return (
            <ReportsDetailWrap>
                <p style={{ color: "rgba(226,29,37,1)", padding: "20px 0" }}>⚠ {error}</p>
            </ReportsDetailWrap>
        );
    }

    // ─── No data ─────────────────────────────────────────────────────────────
    if (!order) {
        return (
            <ReportsDetailWrap>
                <p style={{ color: "rgba(143,143,143,1)", padding: "20px 0" }}>No order selected.</p>
            </ReportsDetailWrap>
        );
    }

    // ─── Transaction detail rows ─────────────────────────────────────────────
    const transactionDetails = [
        { label: "Order Number",     value: order.trackingId || String(order.orderId).slice(-8).toUpperCase() },
        { label: "Payment Method",   value: PAYMENT_LABELS[order.paymentMethod] || order.paymentMethod || "—" },
        { label: "Customer",         value: order.customer?.name || "Guest" },
        { label: "Total Items",      value: `${order.totalItems || 0} Items` },
        { label: "Date & Time",      value: formatDateTime(order.createdAt) },
        { label: "Total Sale",       value: `PKR ${Number(order.pricing?.total || 0).toFixed(2)}`, highlight: true },
    ];

    return (
        <ReportsDetailWrap>
            {/* Transaction summary */}
            <div className="transaction-detail">
                <h2>Transaction Detail:</h2>
                <div className="detail-wrap">
                    {transactionDetails.map((detail, idx) => (
                        <div key={idx} className="card">
                            <strong>{detail.label}</strong>
                            <p style={{ color: detail.highlight ? "rgba(39,196,83,1)" : "inherit", fontWeight: detail.highlight ? 600 : 300 }}>
                                {detail.value}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Pricing breakdown */}
            {order.pricing && (
                <div className="transaction-detail" style={{ marginBottom: 20 }}>
                    <h2 style={{ marginBottom: 15 }}>Pricing Breakdown:</h2>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                        {[
                            { label: "Subtotal",     value: `PKR ${Number(order.pricing.subTotal).toFixed(2)}` },
                            { label: "Delivery Fee", value: `PKR ${Number(order.pricing.deliveryFee).toFixed(2)}` },
                            { label: "Tax",          value: `PKR ${Number(order.pricing.tax).toFixed(2)}` },
                            { label: "Tip",          value: `PKR ${Number(order.pricing.tip).toFixed(2)}` },
                            { label: "Discount",     value: Number(order.pricing.discount) > 0 ? `-PKR ${Number(order.pricing.discount).toFixed(2)}` : `PKR 0.00` },
                            { label: "Total",        value: `PKR ${Number(order.pricing.total).toFixed(2)}`, highlight: true },
                        ].map((row, i) => (
                            <div key={i} className="card" style={{ fontWeight: row.highlight ? 600 : 300 }}>
                                <strong style={{ fontSize: 13, color: "rgba(119,119,119,1)" }}>{row.label}</strong>
                                <p style={{ color: row.highlight ? "rgba(39,196,83,1)" : "inherit" }}>{row.value}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Purchased items */}
            <div className="purchase-items">
                <h2 className="title">Purchased Items:</h2>
                {order.items?.length > 0 ? (
                    <div className="purchase-card">
                        {order.items.map((item, index) => (
                            <div key={index} className="card">
                                <div className="image-wrap">
                                    {item.image ? (
                                        <img src={item.image} alt={item.name} style={{ width: 94, height: 80, objectFit: "cover", borderRadius: 8 }} />
                                    ) : (
                                        <div style={{ width: 94, height: 80, background: "rgba(236,236,236,0.7)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>
                                            🍽️
                                        </div>
                                    )}
                                </div>
                                <div className="text-wrap">
                                    <strong>{item.name || "—"}</strong>
                                    <p>Category: <span>{item.category || "—"}</span></p>
                                    <p>Qty: {item.quantity} × <span>${Number(item.unitPrice || 0).toFixed(2)}</span></p>
                                    {item.notes && <p style={{ color: "rgba(143,143,143,1)", fontSize: 12 }}>{item.notes}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p style={{ color: "rgba(143,143,143,1)" }}>No items found.</p>
                )}
            </div>
        </ReportsDetailWrap>
    );
};

export default RepotsDetail;
